import React, { useEffect, useState } from "react"
import Pagination from "../../components/Pagination"
import axios from "axios"
import Spinner from "../../components/Spinner"
import { BASE_URL, fetchAll, fetcher } from "../../utils/api"
import { ErrorNotification, SuccessNotification } from "../../utils"
import DashboardHeader from "../../components/DashboardHeader"
import FilterWithSearchAndRegion from "../../components/Filters/FilterWithSearchAndRegion"
import { FiEye, FiPlusCircle } from "react-icons/fi"
import RenderNoProcessor from "../../components/NoData/RenderNoProcessor"
import { Tooltip } from "../../layout/sidebar"
import { IoArrowBack, IoArrowForward } from "react-icons/io5"
import AddProcessorModal from "../../components/modals/AddProcessorModal"
import ProcessorDetailsModal from "../../components/modals/ProcessorDetailsModal"
import DeleteProcessorModal from "../../components/modals/DeleteProcessorModal"
import useSWR from "swr"

const Index = () => {
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10) // Added pageSize
  const [count, setCount] = useState(0)
  const [sendSMSLoading, setSMSLoading] = useState(false)
  const [view, setView] = useState("list") // list | map
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data: mills, isLoading, mutate } = useSWR(
    `provider-processor/connections/`,
    () => fetchAll(`provider-processor/connections/`)
  )

  const initialState = {
    region: "",
    district: "",
    community: "",
  }
  const [states, setState] = useState(initialState)

  // --- Filtering and Search Logic ---
  const getFilteredMills = () => {
    let data = mills || []
    // Filter by region, district, community
    if (states.region) {
      data = data.filter(
        (el) => el?.processor?.region?.toLowerCase().includes(states.region.toLowerCase())
      )
    }
    if (states.district) {
      data = data.filter(
        (el) => el?.processor?.district?.toLowerCase().includes(states.district.toLowerCase())
      )
    }
    if (states.community) {
      data = data.filter(
        (el) => el?.processor?.community?.toLowerCase().includes(states.community.toLowerCase())
      )
    }
    // Search by processor name
    if (search) {
      data = data.filter(
        (el) => `${el?.processor?.firstname} ${el?.processor?.lastname}`.toLowerCase().includes(search.toLowerCase())
      )
    }
    return data
  }

  // --- Pagination Logic ---
  const filteredMills = getFilteredMills()
  const totalPages = Math.ceil(filteredMills.length / pageSize)
  const paginatedMills = filteredMills.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  useEffect(() => {
    setCount(totalPages)
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [filteredMills.length, pageSize])

  // --- Pagination Handlers ---
  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }
  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  // --- Suggestions for Search ---
  const suggestions = mills?.map(mill => `${mill?.processor?.firstname} ${mill?.processor?.lastname}`) || []

  function handleChange(evt) {
    const value = evt.target.value
    setState({
      ...states,
      [evt.target.name]: value,
    })
  }

  const resetFilters = () => {
    setSearch("")
    setState(initialState)
    setCurrentPage(1)
  }

  const handleDeleteProcessor = async () => {
    if (!selected) return
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem("token")
      await axios.delete(
        `${BASE_URL}provider-processor/connections/${selected.id}/remove-connection/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      setShowDeleteModal(false)
      setShowDetailsModal(false)
      setDeleteLoading(false)
      // Optionally show a notification
      if (typeof SuccessNotification === 'function') SuccessNotification("Processor deleted successfully")
      // Refresh the list
      mutate()
    } catch (error) {
      setDeleteLoading(false)
      // Optionally show an error notification
      ErrorNotification(error?.message || "Failed to delete processor.")
    }
  }


  return (
    <>

      {showAddModal && <AddProcessorModal
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        selected={selected}
        mutate={mutate}
      />}


      {showDetailsModal && <ProcessorDetailsModal
        showDetailsModal={showDetailsModal}
        setShowDetailsModal={setShowDetailsModal}
        selected={selected}
        loading={deleteLoading}
        onDelete={handleDeleteProcessor}
      />}

      {showDeleteModal && <DeleteProcessorModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        selected={selected}
        loading={deleteLoading}
        onDelete={handleDeleteProcessor}
      />}


      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Processors" />


            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={() => {
                  setShowAddModal(true)
                }}
                className="bg-yellowcolor capitalize hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md"
              >
                <FiPlusCircle size={20} />
                Add new Processor
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <FilterWithSearchAndRegion
              placeholder="Search processors"
              suggestions={suggestions}
              search={search}
              setSearch={value => {
                setSearch(value)
              }}
              onSearch={value => {
                setSearch(value)
                setCurrentPage(1)
              }}
              view={view}
              setView={setView}
              handleChange={e => {
                handleChange(e)
                setCurrentPage(1)
              }}
              hideMap={true}
            />

            {(search || states.region || states.district || states.community) && (
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="my-5">
                  {filteredMills.length > 0 ? (
                  <>
                    <div className="my-5">
                        <div className="flex flex-col">
                          <div className="-m-1.5 overflow-x-auto">
                            <div className="p-1.5 min-w-full inline-block align-middle">
                              <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                                  <thead className="bg-transparent dark:bg-secondaryDark">
                                  <tr>
                                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Processor</th>
                                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Factory Name</th>
                                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Phone No./ Email</th>
                                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Primary Commodity</th>
                                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Region</th>
                                      <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Community</th>
                                      <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500  capitalize hover:text-black ">Action</th>
                                  </tr>
                                </thead>
                                  <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                                    {paginatedMills.map((data) => (
                                    <tr key={data.id} className="text-[13px]">
                                      <td className="px-6 hover:bg-gray-50 dark:hover:bg-secondaryDark py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2 capitalize">
                                        {data?.processor?.photo_url?.length > 0 ? <img
                                          src={data?.processor?.photo_url}
                                          className="w-10 h-10 flex overflow-hidden rounded-full" /> : <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                                          <p className="text-sm uppercase font-bold">
                                            {data?.processor?.firstname?.charAt(0)} {data.processor?.lastname?.charAt(0)}
                                          </p>
                                        </div>}
                                        {/*
                                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                                          <p className="text-sm uppercase font-bold">
                                            {data?.processor?.firstname?.charAt(0)} {data.processor?.lastname?.charAt(0)}
                                          </p>
                                        </div>
                                        {`${data?.processor?.firstname} ${data?.processor?.lastname}`} */}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {data?.processor?.is_business_account ? data?.processor?.business_name : data?.processor?.farm_name || "N/A"}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                          <p className="capitalize">
                                            {data?.processor?.phone || "N/A"}
                                          </p>
                                          <p className="lowercase text-xs">
                                            {data?.processor?.email || "N/A"}
                                          </p>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {data?.processor?.crop || "N/A"}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {data?.processor?.region || "N/A"}
                                      </td>

                                      <td className="px-6 py-4 whitespace-nowrap font-light">
                                        {data?.processor?.community || "N/A"}
                                      </td>

                                      <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                        <div className="flex flex-row items-center gap-x-4">
                                          <Tooltip text="View" position="top">
                                            <button
                                              onClick={() => {
                                                setSelected(data)
                                                setShowDetailsModal(true)
                                              }}
                                              className="">
                                              <FiEye size={16} />
                                            </button>
                                          </Tooltip>
                                          <Tooltip text="Delete" position="top">
                                            <button
                                              onClick={() => {
                                                setSelected(data)
                                                setShowDeleteModal(true)
                                              }}
                                              className="">
                                              <svg width="20" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                              </svg>
                                            </button>
                                          </Tooltip>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>

                              </table>
                                <Pagination
                                  currentPage={currentPage}
                                  totalPages={totalPages}
                                  onPageChange={handlePageClick}
                                />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center p-10 bg-white dark:bg-dark rounded-lg border border-gray-200 dark:border-border-dark">
                    <p className="text-gray-500 mb-4">No Processors found</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>


    </>
  )
}


export default Index
